import React, { ReactNode } from 'react';
import { formatStringList } from '../automation/decommissioning/decom-policy-form/decom-policy-utils';
import { sizeComparisonOptions } from '../../shared/file-size-utils';
import { PolicyAction } from './policy-dialog';
import {
  actionPopOptions,
  enablePopFor,
} from './policy-actions/policy-field-options';
import { profileFieldOptions } from '../../shared/shared-profile-field-filter';
import { Policy } from '../../../types';
import { UpdateMobileDeviceActionType } from './policy-actions/update-mobile-devices';
import { AddShareDriveActionType } from './policy-actions/add-share-drive';
import { RemoveDomainFindableSharesActionType } from './policy-actions/remove-domain-findable-shares';
import { RemoveSpecificSharesActionType } from './policy-actions/remove-specific-shares';
import { AddDelegateActionType } from './policy-actions/add-email-delegate';
import { AddSendAsActionType } from './policy-actions/add-send-as';
import { ChangeImapActionType } from './policy-actions/change-imap';
import { ChangePopActionType } from './policy-actions/change-pop';
import { SetForwardingActionType } from './policy-actions/set-forwarding';
import { CreateGmailLabelActionType } from './policy-actions/create-gmail-label';
import { RemoveEmailDelegateActionType } from './policy-actions/remove-email-delegate';
import { AddToGroupActionType } from './policy-actions/add-to-group';
import { DuplicateUserGroupsActionType } from './policy-actions/duplicate-user-groups';
import { RemoveFromGroupActionType } from './policy-actions/remove-from-group';
import { ChangeGpanelRoleActionType } from './policy-actions/change-gPanel-role';
import { ChangeCpanelRoleActionType } from './policy-actions/change-cPanel-role';
import { ChangeContactSharingActionType } from './policy-actions/change-contact-sharing';
import { LastLoginActionType } from './policy-actions/last-login';
import { ReassignAliasesActionType } from './policy-actions/reassign-alliases';
import { SetOrganizationActionType } from './policy-actions/set-organization';
import { SuspendUserActionType } from './policy-actions/suspend-user';
import { SuspendInactiveUsersActionType } from './policy-actions/suspend-inactive-users';
import { DynamicGroupAssignmentActionType } from './policy-actions/dynamic-group-assignment';
import { CreateGmailFilterActionType } from './step-three';
import { SetProfileInfoActionType } from './set-profile-info-step';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

const highlight = (text: string) => (
  <Box component="span" sx={{ fontWeight: 'bold' }}>
    {text}
  </Box>
);

const simpleSentence = (start: string, value?: string, end = '') => (
  <Box component="span">
    {start}
    {value && highlight(value)}
    {end}
  </Box>
);

export const policyNarratives: Record<
  string,
  | string
  | ((action: PolicyAction, selectedPolicy?: Partial<Policy>) => ReactNode)
> = {
  Clear_Primary_Calendar: () =>
    "Events will be cleared from the user's primary calendar.",
  Update_Mobile_Devices: (action: UpdateMobileDeviceActionType) => {
    const ops = [
      action.remoteWipe === 'true' && 'Remote Wipe Devices',
      action.remoteAccountWipe === 'true' && 'Remove Account Wipe Devices',
      action.block === 'true' && 'Block Devices',
      action.delete === 'true' && 'Delete Devices',
    ].filter(Boolean) as string[];
    const chosenOps = ops.length > 0 ? formatStringList(ops) : null;
    const days = action.daysBack ?? 0;
    const plural = days === 1 ? 'day' : 'days';

    return (
      <>
        Mobile devices will be updated automatically after{' '}
        {highlight(days + ' ' + plural + ' of last sync')}
        {chosenOps ? (
          <>
            {' '}
            and will perform the following operations: {highlight(chosenOps)}.
          </>
        ) : (
          '.'
        )}
      </>
    );
  },
  Add_To_Shared_Drive: (action: AddShareDriveActionType) => {
    const chosenDrive =
      action.driveName && action.driveName.length > 0
        ? formatStringList(action.driveName.map((d: string) => d))
        : '';
    return (
      <>
        The policy user(s) will be added to{' '}
        {action.driveName ? (
          action.driveName[0] === '*' ? (
            highlight('All Shared Drives')
          ) : (
            <>the following shared drive/s: {highlight(chosenDrive)}</>
          )
        ) : (
          ''
        )}
        .
      </>
    );
  },
  Remove_Domain_Findable_Shares: (
    action: RemoveDomainFindableSharesActionType
  ) => {
    const accessType = [
      action.removeSearchableShares === true && 'Can Find in Search Results',
      action.removeDomainFindableShares === true && 'Must Have Link Access',
    ].filter(Boolean) as string[];
    const chosenAccessType =
      accessType.length > 0 ? formatStringList(accessType) : null;

    return (
      <>
        Domain findable shares that{' '}
        {chosenAccessType ? <>{highlight(chosenAccessType)}</> : ''} will be
        removed.
      </>
    );
  },
  Remove_Public_Shares: () => "The user's public shares will be removed.",
  Remove_Specific_Shares: (action: RemoveSpecificSharesActionType) => {
    const address =
      action.externalshare && action.externalshare.length > 0
        ? formatStringList(action.externalshare.map((d: string) => d))
        : '';
    return (
      <>
        The user's specific shares granted by {highlight(address)} will be
        removed.
      </>
    );
  },
  Add_Send_As: (action: AddSendAsActionType) =>
    simpleSentence(
      'Send as domain(s) added for: ',
      formatStringList(action.domainList ?? []),
      '.'
    ),
  Apply_Signature_Template: () =>
    "The selected signature template will be applied to the new user's Gmail account.",
  Add_Delegate_To_Account: (action: AddDelegateActionType) => {
    const days = action.daysUntilExpiration ?? 0;
    const plural = days === 1 ? 'day' : 'days';
    return (
      <>
        {highlight(formatStringList(action.addDelegateToAccount ?? []))} will be
        added as Gmail delegate
        {days > 0 && (
          <> and will expire after {highlight(days + ' ' + plural)}</>
        )}
        .
      </>
    );
  },
  Change_IMAP: (action: ChangeImapActionType) =>
    simpleSentence(
      'IMAP will be set to ',
      action.gmailIMAP === 'true' ? 'enabled' : 'disabled',
      '.'
    ),
  Change_POP: (action: ChangePopActionType) => {
    const gmailPOPEnabledFor =
      enablePopFor.find((a) => a.value === action.gmailPOPEnabledFor)
        ?.displayValue ?? '';
    const gmailPOPAction =
      actionPopOptions.find((a) => a.value === action.gmailPOPAction)
        ?.displayValue ?? '';
    return (
      <>
        POP{' '}
        {highlight(
          action.gmailPOPEanbled === 'true' && action.gmailPOPEnabledFor
            ? 'for ' + gmailPOPEnabledFor
            : ''
        )}{' '}
        will be{' '}
        {highlight(action.gmailPOPEanbled === 'true' ? 'enabled' : 'disabled')}
        {action.gmailPOPEanbled === 'true' ? (
          <> then {highlight(gmailPOPAction)}</>
        ) : (
          ''
        )}
        .
      </>
    );
  },
  Set_Forwarding: (action: SetForwardingActionType) =>
    simpleSentence('', action.destUser, ' will be set as forwarding address.'),
  Create_Gmail_Label: (action: CreateGmailLabelActionType) =>
    simpleSentence(
      '',
      formatStringList(action.gmailLabel ?? []),
      ' will be added as Gmail labels.'
    ),
  Remove_Delegate_From_Account: (action: RemoveEmailDelegateActionType) =>
    simpleSentence(
      action.removeAllDelegatesEnabled ? '' : 'The delegate/s: ',
      action.removeAllDelegatesEnabled
        ? 'All delegates'
        : formatStringList(action.removeDelegateFromAccount ?? []),
      " will be removed from the user's email."
    ),
  Add_To_Group: (action: AddToGroupActionType) =>
    simpleSentence(
      'User will be added to group(s): ',
      formatStringList(action.groupEmail ?? []),
      '.'
    ),
  Duplicate_User_Groups: (action: DuplicateUserGroupsActionType) =>
    simpleSentence('User groups duplicated from: ', action.srcUser, '.'),
  Remove_From_Group: (action: RemoveFromGroupActionType) => {
    const groupEmail = formatStringList(action.groupEmail ?? []);
    let value: string;

    if (action.removeAllGroups === 'true') {
      value =
        action.groupEmail && action.groupEmail.length > 0
          ? 'All Groups except ' + groupEmail
          : 'All Groups';
    } else {
      value = groupEmail;
    }

    return simpleSentence(
      'User will be removed from ' +
        (action.removeAllGroups !== 'true' ? 'group(s): ' : ''),
      value,
      '.'
    );
  },
  Change_gPanel_Role: (action: ChangeGpanelRoleActionType) =>
    simpleSentence('User gPanel role changed to ', action.roleName, '.'),
  Change_cPanel_Role: (action: ChangeCpanelRoleActionType) =>
    simpleSentence('User cPanel role changed to ', action.cPanelIsAdmin, '.'),
  Change_Contact_Sharing: (action: ChangeContactSharingActionType) =>
    simpleSentence(
      'Contact sharing will be set to ',
      action.contactSharing === 'true' ? 'enabled' : 'disabled',
      '.'
    ),
  Disable_Two_Factor: () =>
    simpleSentence('Two Factor Authentication will be ', 'disabled', '.'),
  Force_Password_Reset: () => "User's password will be reset.",
  Hide_From_Directory: () =>
    'User will be removed from the Global Address list.',
  Last_Login_Action: (action: LastLoginActionType) => {
    const days = action.daysNotLoggedIn ?? 0;
    const plural = days === 1 ? 'day' : 'days';
    const lastLoginActionType =
      action.lastLoginActionType === 'suspend' ? 'suspended' : 'deleted';
    return (
      <>
        Accounts not logged in after {highlight(days + ' ' + plural)} will be{' '}
        {highlight(lastLoginActionType)}.
      </>
    );
  },
  Reassign_Aliases: (action: ReassignAliasesActionType) =>
    simpleSentence(
      'Gmail aliases will be transferred to ',
      action.destUser ?? '',
      '.'
    ),
  Set_Organization: (action: SetOrganizationActionType) =>
    simpleSentence(
      'User organization will be set to ',
      action.orgUnit ?? '',
      '.'
    ),
  Sign_Out_User: () =>
    'User will be signed out from all web and device sessions and sign-in cookies reset.',
  Suspend_User: (action: SuspendUserActionType) =>
    simpleSentence(
      'User will be suspended',
      action.resetDecom === 'true' ? ' and decom status reset' : '',
      '.'
    ),
  Unsuspend_User: () => 'User will be unsuspended.',
  Suspend_Inactive_Users: (action: SuspendInactiveUsersActionType) =>
    simpleSentence(
      'User will be suspended if inactive for ',
      action.daysBack + ' days',
      '.'
    ),
  Dynamic_Group_Assignment: (action: DynamicGroupAssignmentActionType) => {
    const profileField = profileFieldOptions.find(
      (o) => o.value === action.dynamicGroupAssignment?.profileFieldType
    )?.display;
    return (
      <>
        Group assignments will be automated based on{' '}
        {highlight(profileField ?? '')}
        {action.dynamicGroupAssignment?.regEx &&
          highlight(' (RegEx: ' + action.dynamicGroupAssignment.regEx + ')')}
        {action.dynamicGroupAssignment?.profileField &&
          highlight(
            ` (${
              action.dynamicGroupAssignment.exact ? 'Exact ' : ''
            }Field Value: ${action.dynamicGroupAssignment.profileField})`
          )}{' '}
        with group(s) mapped to{' '}
        {highlight(
          formatStringList(action.dynamicGroupAssignment?.groupEmail ?? [])
        )}
        .
      </>
    );
  },
  Create_Gmail_Filter: (action: CreateGmailFilterActionType) => {
    if (!action.gmailFilter) return '';
    const criteria = action.gmailFilter.criteria;
    const actionFilter = action.gmailFilter.action;

    const fields = [
      { label: 'From: ', value: criteria.from },
      { label: 'To: ', value: criteria.to },
      { label: 'Subject: ', value: criteria.subject },
      { label: 'Has the words: ', value: criteria.query },
      { label: "Doesn't have: ", value: criteria.negatedQuery },
      { label: 'Has Attachment: ', value: criteria.hasAttachment ? 'Yes' : '' },
      ...(criteria.size > 0
        ? [
            {
              label: 'Size Comparison: ',
              value:
                sizeComparisonOptions.find(
                  (s) => s.value === criteria.sizeComparison
                )?.displayValue +
                ' ' +
                criteria.size +
                ' ' +
                criteria.sizeType,
            },
          ]
        : []),
    ].filter((f) => f.value && f.value.trim() !== '');

    const forward = actionFilter?.forward ?? '';
    const addLabelIds = actionFilter?.addLabelIds ?? [];
    const removeLabelIds = actionFilter?.removeLabelIds ?? [];

    const ops = [
      forward.trim() !== '' && `Forward to address(${forward})`,
      addLabelIds.includes('STARRED') && 'Star Email',
      addLabelIds.includes('TRASH') && 'Trash Email',
      removeLabelIds.includes('INBOX') && 'Skip Inbox',
      removeLabelIds.includes('UNREAD') && 'Mark Email As Read',
    ].filter(Boolean) as string[];

    return (
      <Box>
        Gmail Filter will be created with the following criteria and actions:
        <Grid container spacing={2} mt={1} gap={2}>
          <Grid size={{ xs: 8 }}>
            <Box mb={1}>{highlight('Criteria')}</Box>
            {fields.map((f) => (
              <Box key={f.label} mb={1}>
                {f.label} {f.value}
              </Box>
            ))}
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Box mb={1}>{highlight('Actions')}</Box>
            {ops.map((a) => (
              <Box key={a} mb={1}>
                {a}
              </Box>
            ))}
          </Grid>
        </Grid>
      </Box>
    );
  },
  Set_Profile_Information: (action: SetProfileInfoActionType) => {
    if (!action.userModelTemplate) return null;
    const profileInfo = action.userModelTemplate;
    const primaryOrganization = profileInfo.organizations?.find(
      (org) => org.primary
    );

    const employeeRelated = [
      {
        label: 'Employee ID: ',
        value:
          profileInfo?.externalIds?.find((id) => id.type === 'organization')
            ?.value ?? '',
      },
      {
        label: 'Work Phone: ',
        value:
          profileInfo?.phones?.find((id) => id.type === 'work')?.value ?? '',
      },
      {
        label: "Manager's Email: ",
        value:
          profileInfo?.relations?.find((id) => id.type === 'manager')?.value ??
          '',
      },
    ].filter((f) => f.value && f.value.trim() !== '');

    const primaryOrgData = (
      primaryOrganization
        ? [
            {
              label: 'Job Title: ',
              value: primaryOrganization.title,
            },
            {
              label: 'Type of Employee: ',
              value: primaryOrganization.description,
            },
            {
              label: 'Department: ',
              value: primaryOrganization.department,
            },
            {
              label: 'Organization Name: ',
              value: primaryOrganization.name,
            },
            {
              label: 'Cost Center: ',
              value: primaryOrganization.costCenter,
            },
            {
              label: 'Location: ',
              value: primaryOrganization.location,
            },
            {
              label: 'Stock Symbol: ',
              value: primaryOrganization.symbol,
            },
          ]
        : []
    ).filter((f) => f.value && f.value.trim() !== '');

    const capitalize = (str: string | undefined) =>
      str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

    const otherRelated = [
      {
        label: 'Addresses: ',
        value: profileInfo.addresses?.map((address) => {
          const addressType =
            address.type !== 'custom' ? address.type : address.customType ?? '';
          if (address.formatted)
            return (
              <Box key={address.formatted}>
                {capitalize(addressType)}: {address.formatted}
              </Box>
            );

          const parts = [
            address.poBox,
            address.extendedAddress,
            address.streetAddress,
            address.locality,
            address.region,
            address.postalCode,
            address.country,
          ].filter(Boolean);

          return (
            <Box key={parts.join(', ')}>
              {capitalize(addressType)}: {parts.join(', ')}
            </Box>
          );
        }),
      },
      {
        label: 'Emails: ',
        value: profileInfo.emails?.map((email) => {
          const emailType =
            email.type !== 'custom' ? email.type : email.customType ?? '';

          return (
            <Box key={email.address}>
              {capitalize(emailType)}: {email.address}
            </Box>
          );
        }),
      },
      ...(profileInfo.gender && profileInfo.gender.type
        ? [
            {
              label: 'Gender:',
              value: `${capitalize(profileInfo.gender.type)} ${
                profileInfo.gender.addressMeAs
                  ? ': ' + profileInfo.gender.addressMeAs
                  : ''
              }`,
            },
          ]
        : []),
      {
        label: 'IDs: ',
        value: profileInfo.externalIds?.map((id) => {
          const idType = id.type !== 'custom' ? id.type : id.customType ?? '';

          return (
            <Box key={id.value}>
              {capitalize(idType)}: {id.value}
            </Box>
          );
        }),
      },
      {
        label: 'IMs: ',
        value: profileInfo.ims?.map((i) => {
          const idType = i.type !== 'custom' ? i.type : i.customType ?? '';

          return (
            <Box key={i.im}>
              {capitalize(idType)}: {capitalize(i.protocol)}
              {i.im}
            </Box>
          );
        }),
      },
      ...(profileInfo.notes && profileInfo.notes.trim() !== ''
        ? [
            {
              label: 'Notes:',
              value: `${profileInfo.notes}`,
            },
          ]
        : []),
      {
        label: 'Organizations: ',
        value: profileInfo.organizations?.map((i) => {
          const orgType =
            i.customType !== 'custom' ? i.type ?? '' : i.customType ?? '';

          return (
            <Box key={i.name}>
              {capitalize(orgType)}: {i.name}
            </Box>
          );
        }),
      },
      {
        label: 'Phones: ',
        value: profileInfo.phones?.map((i) => {
          const phoneType =
            i.customType !== 'custom' ? i.type ?? '' : i.customType ?? '';

          return (
            <Box key={i.value}>
              {capitalize(phoneType)}: {i.value}
            </Box>
          );
        }),
      },
      {
        label: 'Relations: ',
        value: profileInfo.relations?.map((i) => {
          const relationType =
            i.customType !== 'custom' ? i.type ?? '' : i.customType ?? '';

          return (
            <Box key={i.value}>
              {capitalize(relationType)}: {i.value}
            </Box>
          );
        }),
      },
      {
        label: 'Websites: ',
        value: profileInfo.websites?.map((i) => {
          const webType =
            i.customType !== 'custom' ? i.type ?? '' : i.customType ?? '';

          return (
            <Box key={i.url}>
              {capitalize(webType)}: {i.url}
            </Box>
          );
        }),
      },
    ].filter((f) => f.value && f.value.length > 0);

    return (
      <Box>
        User profile information will be set based on provided fields, leaving
        the rest unchanged.
        <Grid container spacing={2} mt={1} gap={2}>
          {employeeRelated.length > 0 ? (
            <Grid size={{ xs: 12 }}>
              <Box mb={1}>{highlight('Employee Related')}</Box>
              <Grid container spacing={1} mt={1}>
                {employeeRelated.map((f) => (
                  <React.Fragment key={f.label}>
                    <Grid size={{ xs: 3 }}>{highlight(f.label)}</Grid>
                    <Grid size={{ xs: 9 }}>{f.value}</Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Grid>
          ) : null}

          {primaryOrgData.length > 0 ? (
            <Grid size={{ xs: 12 }}>
              <Box mb={1}>{highlight('Primary Organization')}</Box>
              <Grid container spacing={1} mt={1}>
                {primaryOrgData.map((f) => (
                  <React.Fragment key={f.label}>
                    <Grid size={{ xs: 3 }}>{highlight(f.label)}</Grid>
                    <Grid size={{ xs: 9 }}>{f.value}</Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Grid>
          ) : null}

          {otherRelated.length > 0 ? (
            <Grid size={{ xs: 12 }}>
              <Box mb={1}>{highlight('Other Information')}</Box>
              <Grid container spacing={1} mt={1}>
                {otherRelated.map((f) => (
                  <React.Fragment key={f.label}>
                    <Grid size={{ xs: 3 }}>{highlight(f.label)}</Grid>
                    <Grid size={{ xs: 9 }}>{f.value}</Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Grid>
          ) : null}
        </Grid>
      </Box>
    );
  },
} as Record<
  string,
  | string
  | ((action: PolicyAction, selectedPolicy?: Partial<Policy>) => ReactNode)
>;
